/**
 * The Lab's config (decision 123; BUILDLIST 67.5): one field per experimental switch, and three pure
 * mappings — to the `MatchSetup` the real-time launch path already takes, to the `?action&…` URL the page
 * already reads (so a report is reproducible and the harness can take the same config), and back from such
 * a URL. No DOM here; `lab.ts` draws the panel over this.
 *
 * The rule (123): every experimental feature ships with its Lab row and a default; an accepted one becomes
 * the default or a Settings entry and leaves the Lab.
 */

import { armourIssueOf } from '../core/armour';
import { LOOK_PERIOD, START_UNWRAPPED, SKY_RISK, SKY_BENDS } from '../constants/action';
import { forcesOf, type MatchSetup } from '../match/setup';
import { doctrineOfStyle, type ComposedForce, type Doctrine } from '../core/forceCompose';
import { parseGunShares } from '../core/weapons';
import { parseLayout, parsePlayerLoadout, parseSquadTypes, squadTypesText, mergedSquadTypes, layoutTextOk } from '../core/loadout';
import type { AutocannonCalibre, GunshipGun, ModeConfig, WinConditionKind, PlanetArchetype, PlanetSizePreset, ScaleProfileName, StructureType } from '../types';
import type { BrainSpec, DuelSetup } from '../ai/actionDuel';
import { skirmishMatchSetup, type SkirmishSetup } from '../ai/actionSkirmish';
import { actionDuelSetup } from '../game/smokeSetup';
import { RANGE_LAYOUT } from '../ai/actionRange';
import { PHYS_DT, WIN_CONDITIONS, OPEN_LAYOUT , TANK_GUN, STRUCTURE_HEALTH } from '../constants';
import { AIM_MODE_DEFAULT, type AimMode } from '../game/aimMode';
import { STAND_DENSITY, type StandDensity } from '../core/biomes';
import type { TreeDrawMode } from '../rendering/treeDrawMode';
import { LAB_SWITCHES, LEGACY_PARAM, sideOf, sided, type LabRow, type BoardChoice, type Sided } from './labRows';
import { frontOf } from './labFront';
export { FRONT_CHOICES, frontOf } from './labFront';

export type LabMode = 'duel' | 'skirmish' | 'range';

export interface LabConfig {
  scale: ScaleProfileName;
  mode: LabMode;
  /** People per side. */
  units: number;
  planetSize: PlanetSizePreset;
  /** 67.41: the battle planet's kind ('auto' = the star-driven pick). */
  planet: 'auto' | PlanetArchetype;
  seed: string;
  /** 62.58 step 2: the per-team dials (labRows.ts `Sided`) — one value for both sides, or [yours, the enemy's]: the bots'
   * difficulty and brain, the team picture, the bots' kit switch, the long guns (share text). They replaced 67.24's
   * …Enemy and …Sides rows, whose keys and parameters still load. */
  difficulty: Sided<'easy' | 'medium' | 'hard' | 'expert'>;
  guns: Sided<string>;
  /** 65.32: each side's squad layout (type:count text; 'none' = dealt as before). */
  /** 65.34: custom squad types (the builder's; `squadtypes=`: key=member/member;…) — the presets with these over them. */
  squadTypes: string;
  /** 65.32: the player's own loadout (comma-separated weapons; 'none' = dealt as the side). */
  loadout: string;
  /** 65.31: the 40 mm's rounds a life (6: the launcher's own). */
  gl40Rounds: number;
  /** 65.31: the lob's dials — × the 40 mm's speed, its share of the felt gravity ('default': GROUND_WEAPONS.gl40's). */
  gl40Speed: 'default' | '0.5' | '0.75' | '1.25' | '1.5' | '2';
  gl40Gravity: 'default' | '0.005' | '0.0075' | '0.015' | '0.02' | '0.03';
  /** 65.31: the wind's share on the lob ('default': ACTION_WIND.gl40). */
  gl40Wind: 'default' | '0' | '0.05' | '0.1' | '0.25' | '0.5' | '1' | '3' | '4';
  /** 65.37: × the player's own fire rate ('1': the gun's). */
  playerFireRate: '1' | '1.25' | '1.5' | '2' | '3';
  /** 69.3b's follow-up: how a dropship leaves its landing. */
  dropshipExit: 'pivot' | 'loop';
  /** 69.52 step 1: a ship called into known air defence (GameConfig.skyRule). */
  skyRule: 'route' | 'refuse' | 'fly';
  /** 69.37 step 1: a play ordered mounted is a mounted assault (GameConfig.mountedAssault). */
  mountedAssault: boolean;
  /** DECISIONS 290 (1): a mounted play's column forms at an assembly point first (GameConfig.convoyAssembly; with Mounted assault). */
  convoyAssembly: boolean;
  /** 69.18: a bot commander fetches its stranded squads home by its wave ship (GameConfig.shipPickup). */
  shipPickup: boolean;
  /** 69.62 step 1: a bot commander's hot rescues (GameConfig.hotRescue). */
  hotRescue: boolean;
  /** 69.52 step 2: both sides' pilots' risk (GameConfig.pilotRisk). */
  pilotRisk: 'cautious' | 'steady' | 'bold';
  /** 69.52 step 2, 69.66: the bends' swing (GameConfig.shipWeave). */
  shipWeave: 'off' | 'low' | 'default' | 'high';
  /** 63.13: each side's gunships (GameConfig.gunships), and the side gun's calibre (GameConfig.gunshipGun); DECISIONS 277 (2): the chin gun's (GameConfig.gunshipChin). */
  gunships: '0' | '1' | '2' | '3';
  /** 69.59 step 1: each side's helicopters (GameConfig.helicopters). */
  helicopters: '0' | '1' | '2';
  /** 69.59 step 3 (DECISIONS 286 (4)): the helicopter calls carry the IR suppressors (GameConfig.heliSuppressor). */
  heliSuppressor: boolean;
  /** 69.74 step 2 (DECISIONS 302): each side's helicopters parked on a helipad behind its base (GameConfig.helipad). */
  helipad: boolean;
  gunshipGun: '40' | '57' | '76';
  gunshipChin: '30' | '20';
  layout: Sided<string>;
  /** Seconds before the mode's time rule decides. */
  time: number;
  /** 62.54 step 5: weather carried by the wind field's cells — fronts that move, fog that pools in low ground. */
  /** 62.58 step 4: the weather's mode — timeline (every match's, the default), cells (62.54 step 5), or off. */
  weatherCells: 'timeline' | 'cells' | 'off';
  /** 67.42: the weather cells' dials — cells born a segment, × their radius, and the segment's seconds ('default' = today's). */
  weatherCellCount: 'default' | '1' | '3' | '4' | '6';
  weatherCellSize: 'default' | '0.5' | '1.5' | '2' | '3';
  weatherSegment: 'default' | '30' | '60' | '240' | '480';
  /** 69.6a: the Foothold board — the two bases this share of the circumference apart ('off' = today's layout). */
  board: BoardChoice;
  /** 63.7: each side's planet-scale buildings stand by its first unit (sized by function at person scale). */
  buildings: boolean;
  forest: boolean;
  /** E5.4's density (core/forestDensity.ts): relief (the default, v104) or flat (the old band). */
  forestDensity: 'flat' | 'relief';
  /** 67.61: jungle where wanted (GameConfig.forestJungle): the share of the wooded ground; '0' none. */
  jungle: '0' | '0.2' | '0.35' | '0.5';
  /** X18 step 2: the installations' jobs act — shield, comms relay, weather station, landing pad. */
  installations: boolean;
  /** 63.4 step 3: trees lose limbs and crown by health; limbs fall as debris. */
  partialDestruction: boolean;
  vehicleZones: boolean;
  /** 63.19: a moving vehicle striking a person does damage (GameConfig.vehicleImpact). */
  vehicleImpact: boolean;
  /** 69.38 step 2a: the APC loop's carriers each to its own let-out slot (GameConfig.letOutSlots; on by default, only off is passed). */
  letOutSlots: boolean;
  /** 63.14 step 2: the team buildings drawn as modules from the kit (presentation; the sim reads the one box). */
  modularBuildings: boolean;
  /** 72.3 step 3: with modular buildings, what loses its support comes down (GameConfig.collapse). */
  collapse: boolean;
  /** 63.14 2b: the terraces under the pre-generated bases (core/terraces.ts). */
  terraces: boolean;
  /** 62.60: the sight march alone at a fine scale. */
  sightMarch: boolean;
  /** 63.14 2b's dial: the least rise under a footing worth a terrace (people); '1.5' the rule, unwritten. */
  terraceRise: '1.5' | '1' | '0.6' | '2';
  /** 63.14 step 6: the planet's settlements laid as neutral houses (core/settlements.ts). */
  settlements: boolean;
  /** §0.11 step B (3): the dressing per region of interest (core/biomes.ts). */
  regionDressing: boolean;
  /** 69.3b, the ship as a body (core/shipDamage.ts). */
  shipsDestructible: boolean;
  /** 67.12: how many stands dress the site (× core/biomes STAND_DENSITY; 'normal' = today). */
  stands: StandDensity;
  /** 67.46: on a site wider than the Lab's, the dressing gathered into patches where the fight goes (core/biomes PATCH). */
  biomePatches: boolean;
  /** 67.13: × the felt gravity a bullet falls by ('default' = the scale's: human 0.6, classic 1). */
  bulletGravity: 'default' | '1' | '0.75' | '0.5' | '0.4';
  fineRelief: 'default' | '1.2' | '0.6' | '0';
  /** 67.13: × every bullet's muzzle speed. */
  bulletSpeed: 'default' | '1' | '1.5' | '2' | '2.5' | '3' | '3.5' | '4';
  /** Decision 134: the share of the walking pace kept scrambling up ground too steep to walk ('default' = 0.3; 'off' = such ground stops you, as before). */
  scramble: 'default' | 'off' | '0.15' | '0.5';
  /** The fine ground drawn near the camera (F1; a presentation flag). */
  farLift: '0' | '0.5' | '0.75';
  /** 67.46 (5): the far woods — a canopy cap a stand past the stand-ins' 800 people (rendering/farCanopy.ts; a presentation flag). */
  farWoods: boolean;
  fineGround: boolean;
  /** 67.10: how the held person's gun answers the crosshair — `solved` elevates for the drop (as the bots aim), `direct` fires along the look line. */
  aim: AimMode;
  /** 65.25a: the player's aim assist cone, degrees ('0': off). */
  aimAssist: '0' | '1' | '2' | '4';
  /** 65.47: the spent cases thrown by the shots near the camera (rendering/brass.ts; a presentation flag; on by default). */
  brass: boolean;
  /** 68.41: the command UI — today's one ring, or layout C (F the squads' ring, G the side's calls, the table's command card). */
  commandLayout: 'today' | 'c';
  /** X33 1b: the corpses — how many a battle keeps, how they go, how long they lie (a presentation flag). */
  corpseCap: number;
  corpseFade: 'sink' | 'fade';
  corpseTime: number;
  /** S7: the spent LAW tubes — seconds one lies, and how many a person leaves at once (a presentation flag; game/lawSpent.ts). */
  tubeTime: number;
  tubeCap: number;
  /** 67.75: each side's motor pool drawn behind its base (rendering/motorPoolPlay.ts); 69.14: no longer drawn alone — it turns the pool on, simulated (as motorPoolStock). */
  motorPool: boolean;
  /** 67.61: × the ground litter's density (a presentation dial; rendering/groundLitter.ts; '0' = off). */
  litter: '0' | '0.5' | '1' | '1.5';
  /** 67.55: × the vehicles' loudness (a presentation dial; audio/vehicleSound.ts). */
  vehicleVolume: '0.5' | '0.75' | '1' | '1.5';
  /** 65.9: supply crates per team (0 = none). */
  supplies: number;
  /** 65.10: crates at the capture points; limited spare rounds for the rifle and the pistol. */
  pointSupplies: boolean;
  /** 69.2b–d: relays, the web and the Build verb. */
  relays: boolean;
  /** 69.9: base point defence (a C-RAM and a CIWS with each base). */
  pointDefence: boolean;
  /** 69.50 step 2a: a forward air-defence site for each base. */
  airDefenceSites: boolean;
  /** 69.50 step 2b: the sites' missiles spent on artillery and mortar rounds too. */
  siteRounds: boolean;
  /** 69.58: the ships' decoy flares (GameConfig.countermeasures) and the gunship's roof turret (GameConfig.shipTurrets). */
  countermeasures: boolean;
  shipTurrets: boolean;
  /** 69.13 (a): three towed guns deployed behind each base. */
  towedGuns: boolean;
  /** 69.10: lighters from orbit to a strip behind each base. */
  lighters: boolean;
  /** 69.2a: objectives known roughly. */
  roughStart: boolean;
  /** 69.50 step 1: story objectives derived from the setup (GameConfig.storyObjectives), and their win under open Operations. */
  storyObjectives: boolean;
  /** 69.61 step 4: the story's carry and transmit objectives too (GameConfig.storyCarry). */
  storyCarry: boolean;
  /** 69.61 step 5: the story's escort objective too (GameConfig.storyEscort; with Mounted assault). */
  storyEscort: boolean;
  /** 69.73 step 2a: each side's intel record (GameConfig.intelRecord). */
  intelRecord: boolean;
  /** 69.47 step 1: the engineer's placed charge, the story's destroy targets toughened (GameConfig.charges). */
  demoCharges: boolean;
  /** 69.73 step 3: what each side knows of the other's base at the start (GameConfig.baseIntel; with the intel record). */
  baseIntel: 'none' | 'area' | 'known';
  /** 69.61 step 6 (DECISIONS 284 (a)): the board HQs' health (GameConfig.hqHealth); 20 is the body's own. */
  hqHealth: number;
  winObjectives: boolean;
  /** 69.4: forward bases (with the web: turns relays on). */
  fobs: boolean;
  /** 69.30 step 1: ammunition at the dumps (the tank's rearm). */
  munitionsSupply: boolean;
  /** 69.31: each dump's arm drawn loading the vehicle it rearms, and the tank's ammunition door (a presentation flag, with munitionsSupply; rendering/rearmArm.ts). */
  rearmArm: boolean;
  /** 69.4 step 5: convoys (with FOBs and a vehicle stock: turns FOBs on). */
  convoys: boolean;
  /** 69.32: the points' crates brought by drone (GameConfig.droneDrops), and each side's drones. */
  droneDrops: boolean;
  supplyDrones: '1' | '2' | '3' | '4' | '6';
  /** 69.4 step 6b: build costs (turns convoys on). */
  buildCosts: boolean;
  /** 69.6b: the skirmish's objectives in this many sectors in order (0 = today's line). */
  sectors: number;
  /** 183 (4): objectives per sector along the board ('even': two in each, today's). */
  sectorLayout: 'even' | '2:3:2' | '2:3:4:3:2';
  /** 69.6d / 69.6e: Operations over the sectors — off, a push (your side attacks), or the tug of war. */
  operations: 'off' | 'push' | 'tug' | 'open';
  /** §0.11 step A: under open Operations, the ways to win that count (any one wins). */
  winHq: boolean;
  winMajority: boolean;
  winTickets: boolean;
  winTime: boolean;
  /** 216 (4): territory and fronts — the field (the table draws it) and, as a way to win, a share held for a time. */
  territory: boolean;
  winTerritory: boolean;
  /** Oct 20 (the owner's stand-in, "% node captured"): under open Operations, this many nodes over the whole planet ('off': today's nine between the bases), and 'nodes' as a way to win at this share ('off': not one). */
  openNodes: 'off' | '9' | '18' | '27' | '36' | '48';
  winNodes: 'off' | '0.5' | '0.6' | '0.75' | '1';
  /** 216: under open Operations, the points' ticket bleed (off by default there). */
  ticketBleed: boolean;
  ammoReserves: boolean;
  /** 69.45: the support role (with ammoReserves). */
  supportRole: boolean;
  /** 125a: beacons. */
  beacons: boolean;
  /** S7: a rocket tube for everyone (X cycles to it). Off by default. */
  rockets: Sided<boolean>;   // (S7: per side — decision 164's cells)
  rocketStock: boolean;
  /** 65.31 step 2: the 40 mm's rounds from the side's stock. */
  gl40Stock: boolean;
  /** 69.4 step 6a: build kits — each side's count of every build kind. */
  buildKits: boolean;
  /** 69.4 step 6c: the ground rules for builds. */
  groundRules: boolean;
  /** 65.15: HE and smoke grenades each person carries (0 = none). */
  /** 65.23: grenades from the side's finite stock. */
  grenadeStock: boolean;
  /** 62.58 step 3: the grenades each person carries, HE and smoke (the setup's grenades { he, smoke }; was two rows). */
  grenades: { he: number; smoke: number };
  /** 65.24: smoke at arm's length — people in one cloud see each other; blind bots step out toward a fight they know of. */
  smokeSight: boolean;
  /** Decision 132: bullets meet people's measured bodies (the stance capsules), not today's sphere. */
  measuredBodies: boolean;
  /** 65.28 (decision 161): a near round suppresses. */
  suppression: boolean;
  /** 65.28, the grip (DECISIONS 163 (a)): what a near round takes — mark (today), beat, weight. */
  suppressionGrip: 'mark' | 'beat' | 'weight';
  /** §5.21 step 7: bursts suppress by their class (with Suppression). */
  burstSuppress: boolean;
  /** 66.8 (decision 160): digging in. */
  entrench: boolean;
  /** 63.6: foliage slows and nudges rounds. */
  foliageBullets: boolean;
  /** 66.7: foliage hides from sight. */
  foliageSight: boolean;
  /** 63.5 (67.25): footsteps heard by the ground. */
  footstepsHeard: boolean;
  /** The bot brain (the same flag the harness takes). */
  brain: Sided<BrainSpec>;
  /** 68.39: each side's airstrikes for the match ('0' = none). */
  airstrikes: Sided<'0' | '1' | '2' | '3' | '4' | '6'>;
  /** DECISIONS 200 step 5: each side's pod drops for the match ('0' = none). */
  pods: Sided<'0' | '1' | '2' | '3' | '4' | '6'>;
  /** 69.3b: each side's dropships for the match ('0' = none). */
  dropships: Sided<'0' | '1' | '2' | '3' | '4' | '6'>;
  /** 68.39: each side's bot commander's pacing of them. */
  airstrikeSpend: Sided<'even' | 'early' | 'late'>;
  /** 69.49: each side's force composed by a doctrine ('off': the rows' counts; 'style': its commander's). */
  forceDoctrine: Sided<'off' | 'style' | Doctrine>;
  /** 69.49: each side's budget for it, a factor of FORCE_BUDGET.base. */
  forceBudget: Sided<'0.5' | '0.75' | '1' | '1.25' | '1.5' | '2'>;
  /** 185: each side's armour; the player's own over it. 65.27: hit locations and their multipliers. */
  armour: Sided<'none' | 'helmets' | 'heavyHelmets' | 'light' | 'heavy' | 'heavyKit'>;
  yourArmour: 'none' | 'helmet' | 'heavyHelmet' | 'light' | 'heavy' | 'heavyKit';
  hitLocations: boolean;
  hitHead: '2.5' | '1.5' | '2' | '3' | '4' | '5';
  hitLimbs: '0.6' | '0.4' | '0.5' | '0.75' | '1';
  /** Information towers per team (I1), drones per unit (I2), empty emplacements per team (S6). */
  towers: number;
  /** 62.58 step 5: drones — a unit's, a squad's (the sector's carry, 62.3), and the charges a person carries for them (65.19). */
  drones: { unit: number; squad: number; charges: number };
  emplacements: number;
  /** X31: motorcycles per team, parked by its first unit (gas and electric alternating). */
  bikes: number;
  /** 63.1d: transports (the 6×6) per team. */
  transports: number;
  /** 197 step 1: tracked carriers (the M113) per team. */
  tracked: number;
  tanks: number;
  tankHe: number;
  tankSolid: number;
  /** 63.1u: smoke charges per vehicle (no reload). */
  vehicleSmoke: number;
  /** 63.1u: the bots pop the dispensers too. */
  botVehicleSmoke: boolean;
  /** 69.46: the crewman (the vehicle specialist). */
  crewmanRole: boolean;
  vehicleStock: number;
  /** 63.1n with 67.75: the spares as frames in each side's motor pool (GameConfig.motorPool; DECISIONS 300: on by default — with no stock it stands no pool; the drawn Motor pool row turns the stock on at 2). */
  motorPoolStock: boolean;
  /** 69.19: each side's start fleet unwrapped (GameConfig.startUnwrapped): auto by the roles, all, some (half of each kind) or none. */
  unwrapped: Sided<'auto' | 'all' | 'some' | 'none'>;
  /** 63.1o: technicals (a light truck, a .50 in its bed) per team. */
  technicals: number;
  /** 69.21: what the technicals' mounts carry — the HMG, the rocket, the missile, or a mix cycled over a side's technicals. */
  technicalArmament: 'hmg' | 'rocket' | 'missile' | 'mix';
  /** 69.21 (240): the mounted launchers' store, 2–6 rounds ('default': 4). */
  mountStore: 'default' | '2' | '3' | '4' | '5' | '6';
  /** 69.23: what the tracked carriers carry — the .50 as today, the mortar (the mortar carrier: called-in fire), or a mix cycled over a side's. */
  trackedArmament: 'hmg' | 'mortar' | 'mix';
  /** 69.23: the mortar carrier's store ('default': 12). */
  mortarStore: 'default' | '6' | '9' | '12' | '18' | '24';
  /** 65.22: emplaced HMGs per team. */
  hmgs: number;
  /** K2: the team picture — the bots share what they see, and your markers and map show the team's beliefs (never the truth). */
  picture: Sided<boolean>;
  /** 68.10: fluid squad membership. */
  fluidSquads: boolean;
  /** 70.3: cover read across the body (GameConfig.coverAcross). */
  coverAcross: boolean;
  /** 66.7: a holding squad takes cover from the enemy (GameConfig.coverMoves). */
  coverMoves: boolean;
  /** 68.9: a bot squad's leader sets its steps. */
  squadLead: boolean;
  /** 62.43: the local steer. */
  /** 63.15 step 1: the jump's own feel, and its dials ('default': JUMP's). */
  jumpFeel: boolean;
  jumpApex: 'default' | '0.4' | '0.5' | '0.7' | '0.8' | '1';
  jumpGravity: 'default' | '6' | '8' | '12' | '15';
  localSteer: boolean;
  /** §0.11 step C: the planet's route graph for long drives and the ship's choice. */
  planetRoutes: boolean;
  /** 216 (4): roads between the settlements and the bases. */
  roads: boolean;
  /** 216: the throw's wind-up and the stances' times in the sim. */
  realTiming: boolean;
  /** 66.3: every squad of three keeps this shape on the move ('none' = today's bots). */
  formation: 'none' | 'column' | 'wedge' | 'line';
  /** 66.4: the stand-in commander for both teams ('none' = uncommanded bots), and whether it issues bounds and flanks. */
  /** 68.13: the diorama stop's tilt-shift (a presentation flag; on by default, the owner's choice in 68.14). */
  tiltShift: boolean;
  commanders: 'none' | 'spread' | 'focus' | 'adaptive';
  tactics: boolean;
  /** 68.4: the bot commanders call their reserve (with reserves on). */
  callReserves: boolean;
  /** 68.22 step 4: the bot commanders form plays. */
  /** 68.22 step 4: the commanders form plays — per side (the owner's question, Oct 13: "Have we tested plays against no plays?"). */
  commanderPlays: Sided<boolean>;
  /** 63.1r: a scouting technical drives into its circle, its squad aboard (on by default). */
  technicalRole: boolean;
  smallCraters: boolean;
  /** §5.21 step 6: bursts throw by their class. */
  burstPush: boolean;
  /** 69.65: the bursts as one model (two radii, hulls by kind). */
  blastModel: boolean;
  /** DECISIONS 200 step 4: forward arrivals by dropship. */
  waveShips: boolean;
  /** 207's follow-up: wave ships — a point within this many people of the pad is walked to. */
  waveWalk: '0' | '30' | '60' | '120';
  /** 69.5f: the commander's pull to the front, and the left-behind flag (on by default). */
  frontPull: boolean;
  /** 181. */
  attention: boolean;
  /** 182 (3). */
  commanderPicture: boolean;
  /** 69.87: the bot commander plans over the Match's squads (GameConfig.commanderSquads). */
  commanderSquads: boolean;
  /** §0.11 step D: the adaptive commanders run their sides as efforts. */
  efforts: boolean;
  /** 182 (2). */
  contactBelief: boolean;
  /** 62.60 lever 2: the squad's quiet tier. */
  quietTier: boolean;
  /** 62.60 lever 2b: the march tier (with the quiet tier). */
  marchTier: boolean;
  /** 62.60 lever 2c: the steady-contact tier. */
  steadyTier: boolean;
  brainRate: boolean;
  /** 62.60, the look period in contact (ms; 200 is today's). */
  lookPeriod: '200' | '300' | '400';
  /** 182 (1). */
  pictureDecay: boolean;
  holdAndPush: Sided<boolean>;
  /** 66.15: the standoff manoeuvre. */
  standoffManoeuvre: Sided<boolean>;
  /** 66.16: the flank that takes the point. */
  flankTakesPoint: Sided<boolean>;
  /** 63.1t: the commander's scouts ride the side's APC to a far circle (on by default). */
  scoutRide: boolean;
  /** 66.5: holding squads take sectors of fire. */
  holding: boolean;
  /** A gun's crew runs for it mid-fight (seats under threat). */
  gunRun: boolean;
  /** Where the guns stand (decision 126): behind the lines (at a person scale), or at hand by the first unit. */
  gunsAt: 'lines' | 'hand';
  /** Decision 128: a gun's shell stock and resupply; the spotter's penalties (at a person scale). Both on by default. */
  gunStock: boolean;
  gunSpotting: boolean;
  /** Lane B's brain-rules row: the bots use their kit (drones, beacons, marks, crates, guns; bandages finished). On by default. */
  botKit: Sided<boolean>;
  /** X22: exits on — the Withdraw button in the in-game settings orders your team's squads out. */
  extraction: boolean;
  /** X19: each side's reserve (0 = off): its respawn pool, arriving at its arrival region instead of appearing. */
  reserves: number;
  /** 67.20 (a): how far apart the two teams start, in person-heights ('auto' = today's seeded layout); the same number spawns the same way every time. */
  front: string;
  /** E5.3: how the biome trees are drawn — instanced in chunks with tiers of detail, or each tree its own model (the old way, an A/B). A presentation flag. */
  trees: TreeDrawMode;
  /** 63.3 step 2: the measured hit shapes drawn over the scene, beside what the sim reads today (a presentation flag). */
  hitShapes: boolean;
  /** 68.17's follow-up: the overlay's contour step — auto, or the corner map's step ÷ 1, 5 or 25. */
  topoDensity: 'auto' | '1' | '5' | '25';
  /** 68.17: the commander's topo overlay at the start (L toggles it in play). */
  topoOverlay: boolean;
  /** 68.42: the table at planet scale, and its projection */
  planetTable: 'off' | 'globe' | 'map';
  radarCoverage: boolean;
  /** 62.58 (§5.14 7.7 step 1): the presentation system's master — off, its rows are their defaults; on, as set. */
  presentationDials: boolean;
}

/** The defaults, from the rows (labRows.ts). */
export const LAB_DEFAULTS: LabConfig = Object.fromEntries(LAB_SWITCHES.map(r => [r.key, r.default])) as unknown as LabConfig;
/** 62.58 (§5.14 7.7 step 1): the presentation system's rows, each a dial under its master (`presentationDials`). */
export const PRESENTATION_DIALS: ReadonlyArray<LabRow> = LAB_SWITCHES.filter(r => r.under === 'presentationDials');

export const LAB_STORAGE_KEY = 'orbituary-lab';


/** One row's value from anything (storage, a URL's text), by its control: unknown or malformed falls back to its default; a per-team row's pair side by side. */
export function parseLabValue(r: LabRow, v: unknown): unknown {
  if (r.perTeam && Array.isArray(v) && v.length === 2) return sided(parseOne(r, v[0]), parseOne(r, v[1]));
  return parseOne(r, v);
}
function parseOne(r: LabRow, v: unknown): unknown {
  if (r.kind === 'select') {
    if (r.key === 'operations' && (v === true || v === '1' || v === 'true')) return 'push';   // 69.6e: the row was a toggle (saved configs, ?operations=1)
    // 62.58 step 4: the row was the cells toggle (saved configs, ?weatherCells=1) — on is cells, off today's timeline
    if (r.key === 'weatherCells' && (v === true || v === '1' || v === 'true')) return 'cells';
    if (r.key === 'weatherCells' && (v === false || v === '0' || v === 'false')) return 'timeline';
    return (r.options as readonly unknown[]).includes(v) ? v : r.default;
  }
  if (r.kind === 'number') {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    return Number.isFinite(n) ? Math.min(r.max, Math.max(r.min, Math.round(n))) : r.default;
  }
  if (r.kind === 'counts') {
    // (62.58 step 3: each part a count in the row's range; a part missing or malformed, its default)
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>, d = r.default as Record<string, number>;
    return Object.fromEntries(r.parts.map(({ part }) => {
      const x = o[part], n = typeof x === 'number' ? x : typeof x === 'string' ? Number(x) : NaN;
      const max = r.parts.find(x => x.part === part)?.max ?? r.max;   // (a part's own maximum: the drones' charges)
      return [part, Number.isFinite(n) ? Math.min(max, Math.max(r.min, Math.round(n))) : d[part]];
    }));
  }
  if (r.kind === 'toggle') return typeof v === 'boolean' ? v : v === '1' || v === 'true' ? true : v === '0' || v === 'false' ? false : r.default;
  // a combo row by its own rule: the team distance (67.20 a), or a side's long guns (67.24: kept when it reads as shares)
  if (r.kind === 'combo') return r.key === 'front' ? frontOf(v) : r.key === 'layout' ? (typeof v === 'string' && v.trim() !== 'none' && layoutTextOk(v) ? v.trim() : r.default) : r.key === 'loadout' ? (typeof v === 'string' && parsePlayerLoadout(v) ? parsePlayerLoadout(v)!.join(',') : r.default) : typeof v === 'string' && parseGunShares(v) ? v.trim() : r.default;
  if (r.key === 'squadTypes') return typeof v === 'string' ? squadTypesText(parseSquadTypes(v)) : r.default;   // 65.34: the custom types, whole (no 40-character cut)
  return typeof v === 'string' && v.trim() ? v.trim().slice(0, 40) : r.default;   // text: the seed
}

/** A config from anything JSON-shaped (storage, a pasted object): unknown or malformed fields fall back to the defaults. */
export function normalizeLabConfig(raw: unknown): LabConfig {
  const r = foldSides((raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>);
  const c = Object.fromEntries(LAB_SWITCHES.map(x => [x.key, parseLabValue(x, r[x.key])])) as Record<string, unknown>;
  // 62.58 (§5.14 7.7 step 1): the presentation master. Data from before it (a saved Lab or preset, an old link) has it on
  // when it moved any presentation row; off, the rows are their defaults — what the page draws, and the URL leaves out.
  if (r.presentationDials === undefined) c.presentationDials = PRESENTATION_DIALS.some(x => c[x.key] !== x.default);
  else if (!c.presentationDials) for (const x of PRESENTATION_DIALS) c[x.key] = x.default;
  return c as unknown as LabConfig;
}

/**
 * 62.58 step 2: 67.24's per-side rows (saved configs, presets, old links) folded into the per-team dials — the enemy's
 * difficulty and brain ('same' = as mine), the long guns mine and the enemy's, the side the picture is on for (when on),
 * the side the kit switch turns off (when off; the other keeps it on). A dial already a pair is left as it is.
 */
function foldSides(r: Record<string, unknown>): Record<string, unknown> {
  const o = { ...r };
  const enemy = (base: unknown, e: unknown) => (Array.isArray(base) || e === undefined || e === null || e === 'same' ? base : [base ?? undefined, e]);
  if (!Array.isArray(o.difficulty)) o.difficulty = enemy(o.difficulty ?? 'medium', r.difficultyEnemy);
  if (!Array.isArray(o.brain)) o.brain = enemy(o.brain ?? 'default', r.brainEnemy);
  if (!Array.isArray(o.airstrikes)) o.airstrikes = enemy(o.airstrikes ?? '3', r.airstrikesEnemy);   // 68.39: the enemy's own count
  if (!Array.isArray(o.dropships)) o.dropships = enemy(o.dropships ?? '0', r.dropshipsEnemy);   // 69.3b: the enemy's own count
  if (!Array.isArray(o.pods)) o.pods = enemy(o.pods ?? '0', r.podsEnemy);   // DECISIONS 200 step 5
  if (!Array.isArray(o.airstrikeSpend)) o.airstrikeSpend = enemy(o.airstrikeSpend ?? 'even', r.airstrikeSpendEnemy);
  if (!Array.isArray(o.forceDoctrine)) o.forceDoctrine = enemy(o.forceDoctrine ?? 'off', r.forceDoctrineEnemy);   // 69.49: the enemy's own
  if (!Array.isArray(o.forceBudget)) o.forceBudget = enemy(o.forceBudget ?? '1', r.forceBudgetEnemy);
  if (!Array.isArray(o.armour)) o.armour = enemy(o.armour ?? 'none', r.armourEnemy);   // 185: the enemy's own
  if (!Array.isArray(o.unwrapped)) o.unwrapped = enemy(o.unwrapped ?? 'auto', r.unwrappedEnemy);   // 69.19: the enemy's own
  // (the old keys only come with old data — a saved config or preset merged over today's defaults, which carry `guns`: they win)
  if (r.gunsMine !== undefined || r.gunsEnemy !== undefined) o.guns = [r.gunsMine ?? 'rifle', r.gunsEnemy ?? 'rifle'];
  if (r.layoutMine !== undefined || r.layoutEnemy !== undefined) o.layout = [r.layoutMine ?? 'none', r.layoutEnemy ?? 'none'];   // (65.32: the URL's two halves)
  const on = (v: unknown) => v === true || v === '1' || v === 'true' ? true : v === false || v === '0' || v === 'false' ? false : undefined;
  if (!Array.isArray(o.picture) && (r.pictureSides === 'mine' || r.pictureSides === 'enemy') && on(o.picture ?? true) === true) o.picture = [r.pictureSides === 'mine', r.pictureSides === 'enemy'];
  if (!Array.isArray(o.botKit) && (r.botKitSides === 'mine' || r.botKitSides === 'enemy') && on(o.botKit ?? true) === false) o.botKit = [r.botKitSides !== 'mine', r.botKitSides !== 'enemy'];
  if (!Array.isArray(o.rockets) && (r.rocketSides === 'mine' || r.rocketSides === 'enemy') && on(o.rockets ?? false) === true) o.rockets = [r.rocketSides === 'mine', r.rocketSides === 'enemy'];   // (S7 per side)
  // 62.58 step 3: the grenade rows — HE as a count (`grenades`), smoke its own (`smokeGrenades`) — into the one dial
  if (typeof r.grenades !== 'object' || r.grenades === null || r.smokeGrenades !== undefined) {
    const was = r.grenades && typeof r.grenades === 'object' ? r.grenades as Record<string, unknown> : null;
    o.grenades = { he: was ? was.he : r.grenades ?? 0, smoke: r.smokeGrenades ?? (was ? was.smoke : 0) };
  }
  // 62.58 step 5: the drone rows — drones a unit as a count (`drones`), a squad's (`squadDrones`), the charges (`charges`)
  if (typeof r.drones !== 'object' || r.drones === null || r.squadDrones !== undefined || r.charges !== undefined) {
    const was = r.drones && typeof r.drones === 'object' ? r.drones as Record<string, unknown> : null;
    o.drones = { unit: was ? was.unit : r.drones ?? 0, squad: r.squadDrones ?? (was ? was.squad : 0), charges: r.charges ?? (was ? was.charges : 0) };
  }
  // 66.13: hold what's taken on one side only (`holdpushsides` names it)
  if (!Array.isArray(o.holdAndPush) && (r.holdPushSides === 'mine' || r.holdPushSides === 'enemy')) o.holdAndPush = [r.holdPushSides === 'mine', r.holdPushSides === 'enemy'];
  // 66.16: the flank that takes the point on one side only (`flanktakesides` names it)
  if (!Array.isArray(o.flankTakesPoint) && (r.flankTakeSides === 'mine' || r.flankTakeSides === 'enemy')) o.flankTakesPoint = [r.flankTakeSides === 'mine', r.flankTakeSides === 'enemy'];
  // 66.15: the standoff manoeuvre on one side only (`standoffsides` names it)
  if (!Array.isArray(o.standoffManoeuvre) && (r.standoffSides === 'mine' || r.standoffSides === 'enemy')) o.standoffManoeuvre = [r.standoffSides === 'mine', r.standoffSides === 'enemy'];
  // 68.22: plays on one side only (`playssides` names the side that has them)
  if (!Array.isArray(o.commanderPlays) && (r.playsSides === 'mine' || r.playsSides === 'enemy')) o.commanderPlays = [r.playsSides === 'mine', r.playsSides === 'enemy'];
  for (const k of ['difficultyEnemy', 'brainEnemy', 'gunsMine', 'gunsEnemy', 'pictureSides', 'botKitSides', 'rocketSides', 'playsSides', 'holdPushSides', 'standoffSides', 'flankTakeSides', 'smokeGrenades', 'squadDrones', 'charges']) delete o[k];
  return o;
}

/** What the launch path needs: the setup, and the options `Game.startActionFromSetup` takes. */
export interface LabLaunch {
  setup: MatchSetup;
  humanTeam: number;
  tickLimit: number;
  range: boolean;
}

/** 63.7: the Buildings row's planet-scale buildings, stood by each side's first unit (here and in the URL's `layer0Extras`). */
export const LAB_BUILDINGS: readonly StructureType[] = ['ecotourism', 'shield', 'landingPad', 'antenna'];

/** The experimental switches as `MatchSetup` fields (the same ones the URL's `layer0Extras` sets). */
/** 67.24: is a side's gun text anything but all rifle? */
const guns = (text: string) => { const g = parseGunShares(text); return !!g && Object.keys(g).some(k => k !== 'rifle'); };

/** What labExtras gives the match setup: the setup's fields, plus the skirmish harness's it sets (each part is checked against this). */
export type LabExtras = Partial<MatchSetup> & Pick<SkirmishSetup, 'brain' | 'commanders' | 'tactics' | 'callReserves' | 'holding' | 'knowledgeByTeam' | 'commanderPlays' | 'seatsUnderThreat'>;

/**
 * 69.49: the Lab's force rows as the setup's `forceCompose` — on when a side has a doctrine ('style': its commander's; 'off'
 * beside an on side: that side keeps the rows' counts), each side's budget a factor of the base. Off: nothing.
 */
export function labForceCompose(c: Pick<LabConfig, 'forceDoctrine' | 'forceBudget' | 'commanders'>): Pick<MatchSetup, 'forceCompose'> {
  const doc = (t: 0 | 1): Doctrine | null => { const d = sideOf(c.forceDoctrine, t); return d === 'off' ? null : d === 'style' ? doctrineOfStyle(c.commanders) : d; };
  if (!doc(0) && !doc(1)) return {};
  return { forceCompose: { doctrines: [doc(0), doc(1)], budgets: [Number(sideOf(c.forceBudget, 0)), Number(sideOf(c.forceBudget, 1))] } };
}

/** 69.49: what each side fields with the force rows on — shown under the Doctrine row; '' when off. */
export function labForceText(c: LabConfig): string {
  if (!labForceCompose(c).forceCompose) return '';
  const forces = forcesOf(labLaunch(c).setup as MatchSetup);   // (the launch's own setup: its people, squads and rows)
  const name: Record<string, string> = { transport: '6×6', tracked: 'tracked .50', mortarCarrier: 'mortar carrier', tank: 'tank', technical: 'technical', rocketTechnical: 'rocket technical', missileTechnical: 'missile technical', bike: 'bike', hmg: 'HMG', airstrike: 'airstrike', dropship: 'dropship', pod: 'pod' };
  const side = (f: ComposedForce | undefined): string => !f || !('bought' in f) ? 'the rows\' counts'
    : `${f.doctrine} ${Math.round(f.budget * 10) / 10}: ${Object.entries(f.bought).map(([k, n]) => `${n} ${name[k] ?? k}`).join(', ') || 'nothing'}`;
  return `yours ${side(forces[0] as ComposedForce)} · enemy ${side(forces[1] as ComposedForce)}`;
}

export function labExtras(c: LabConfig): LabExtras {
  // one part a row, each checked alone against LabExtras (62.67: spread into one literal, the checker built the rows'
  // product and ran out of heap at ~120 rows); later parts win, as the spreads did
  const parts: LabExtras[] = [
    c.weatherCells === 'cells' ? { weatherCells: true } : c.weatherCells === 'off' ? { weather: false } : {},
    c.planet !== 'auto' ? { planetArchetype: c.planet } : {},   // 67.41
    c.weatherCellCount !== 'default' ? { weatherCellsPerSegment: Number(c.weatherCellCount) } : {},
    c.weatherCellSize !== 'default' ? { weatherCellScale: Number(c.weatherCellSize) } : {},
    c.weatherSegment !== 'default' ? { weatherSegmentS: Number(c.weatherSegment) } : {},
    c.buildings ? { installationsPerTeam: [...LAB_BUILDINGS] } : {},
    c.installations ? { installations: true } : {},
    c.partialDestruction ? { partialDestruction: true } : {},
    c.vehicleZones ? {} : { vehicleZones: false },   // 63.12; 69.39: on by default in the setup, only off is passed
    c.vehicleImpact ? { vehicleImpact: true } : {},   // 63.19 step 1
    c.letOutSlots ? {} : { letOutSlots: false },   // 69.38 step 2a: on by default, only off is passed
    c.modularBuildings ? { modularBuildings: true } : {},   // 63.14 step 2
    c.collapse ? { collapse: true } : {},   // 72.3 step 3
    c.terraces ? { terraces: true } : {},   // 63.14 2b
    c.sightMarch ? { sightMarch: true } : {},   // 62.60: the sight march alone
    c.terraceRise !== '1.5' ? { terraceRisePeople: Number(c.terraceRise) } : {},   // 63.14 2b's dial
    c.settlements ? { settlements: true } : {},   // 63.14 step 6
    c.regionDressing ? { regionDressing: true } : {},   // §0.11 step B (3)
    c.shipsDestructible ? { shipsDestructible: true } : {},   // 69.3b, the ship as a body
    c.board !== 'off' ? { baseSeparation: Number(c.board) } : {},   // 69.6a
    c.stands !== 'normal' ? { standDensity: STAND_DENSITY[c.stands] } : {},
    c.biomePatches ? {} : { biomePatches: false },   // 67.46 (on by default since step 3)
    c.forest ? {} : { planetForest: false },   // E5.4 step 4: the planet forest in the sim (and drawn: lab.ts) — on by default (the setup's own); off said so
    c.forestDensity === 'flat' ? { forestDensity: 'flat' as const } : {},
    c.jungle !== '0' ? { forestJungle: Number(c.jungle) } : {},   // 67.61   // E5.4's density: by relief by default (the setup's own); flat said so
    c.bulletGravity !== 'default' || c.bulletSpeed !== 'default' || c.scramble !== 'default' || c.fineRelief !== 'default' ? { personTuning: { ...(c.fineRelief !== 'default' ? { fineRelief: Number(c.fineRelief) } : {}), ...(c.bulletGravity !== 'default' ? { bulletGravity: Number(c.bulletGravity) } : {}), ...(c.bulletSpeed !== 'default' ? { bulletSpeed: Number(c.bulletSpeed) } : {}), ...(c.scramble !== 'default' ? { scramble: c.scramble === 'off' ? 0 : Number(c.scramble) } : {}) } } : {},
    c.supplies !== 1 ? { suppliesPerTeam: c.supplies } : {},
    c.pointSupplies ? {} : { pointSupplies: false },
    c.ammoReserves ? { ammoReserves: true } : {},
    c.supportRole && c.ammoReserves ? { supportRole: true } : {},   // 69.45: with the reserves only
    c.beacons ? { beacons: true } : {},
    c.rockets === true ? { rockets: true } : Array.isArray(c.rockets) && c.rockets.some(Boolean) ? { rockets: [...c.rockets] as boolean[] } : {},   // (S7: per side)
    c.rocketStock ? { rocketStock: true } : {},   // 65.29 (2)
    c.gl40Stock ? {} : { gl40Stock: false },   // 65.31 step 2;/ 300: on by default in the setup, only off is passed
    c.buildKits ? { buildKits: true } : {},   // 69.4 step 6a
    c.groundRules ? { groundRules: true } : {},   // 69.4 step 6c
    c.grenades.he > 0 || c.grenades.smoke > 0 ? { grenades: { he: c.grenades.he, smoke: c.grenades.smoke } } : {},
    c.smokeSight ? {} : { smokeSight: false },   // 65.24;/ 300: on by default in the setup, only off is passed
    c.grenadeStock ? { grenadeStock: true } : {},   // 65.23
    c.measuredBodies ? { measuredBodies: true } : {},
    c.suppression ? {} : { suppression: false },   // 65.28;/ 300: on by default in the setup, only off is passed
    c.suppression && c.suppressionGrip !== 'mark' ? { suppressionGrip: c.suppressionGrip } : {},   // 65.28, the grip
    c.suppression && c.burstSuppress ? { burstSuppress: true } : {},   // §5.21 step 7
    c.entrench ? { entrench: true } : {},   // 66.8
    c.foliageBullets ? { foliageBullets: true } : {},
    c.foliageSight ? { foliageSight: true } : {},
    c.footstepsHeard ? { footstepsHeard: true } : {},   // 63.5
    Array.isArray(c.brain) ? { brain: [...c.brain] as BrainSpec[] } : c.brain !== 'default' ? { brain: c.brain as BrainSpec } : {},   // 67.24: each side its brain
    c.towers > 0 ? { towersPerTeam: c.towers } : {},
    c.drones.unit > 0 ? { dronesPerUnit: c.drones.unit } : {},
    c.drones.charges > 0 ? { chargesPerUnit: c.drones.charges } : {},
    c.emplacements > 0 ? { emplacementsPerTeam: c.emplacements } : {},
    c.bikes > 0 ? { bikesPerTeam: c.bikes } : {},
    c.transports > 0 ? { transportsPerTeam: c.transports } : {},
    c.tracked > 0 ? { trackedPerTeam: c.tracked } : {},   // 197 step 1
    c.tanks > 0 ? { tanksPerTeam: c.tanks } : {},   // 69.29
    c.tankHe !== TANK_GUN.stowage.he || c.tankSolid !== TANK_GUN.stowage.solid ? { tankStowage: { he: c.tankHe, solid: c.tankSolid } } : {},   // 69.29: the stowage dial
    c.tracked > 0 && c.trackedArmament !== 'hmg' ? { trackedArmaments: c.trackedArmament === 'mix' ? (['mortar', 'hmg'] as const) : ['mortar' as const] } : {},   // 69.23
    c.mortarStore !== 'default' ? { mortarStore: Number(c.mortarStore) } : {},   // 69.23
    c.vehicleSmoke > 0 ? { vehicleSmoke: c.vehicleSmoke } : {},   // 63.1u
    c.botVehicleSmoke ? { botVehicleSmoke: true } : {},   // 63.1u: off by default
    c.crewmanRole ? {} : { crewmanRole: false },   // 69.46;/ 300: on by default in the setup, only off is passed
    c.vehicleStock > 0 ? { vehicleStock: c.vehicleStock } : {},   // 63.1n
    c.motorPoolStock || c.motorPool ? (c.motorPool && !(c.vehicleStock > 0) ? { motorPool: true, vehicleStock: 2 } : {}) : { motorPool: false },   // 63.1n with 67.75; 69.14: the drawn pool is the simulated one (the Motor pool row, with a stock of 2 if none); 300: the stock's pool on by default in the setup, only off is passed
    c.technicals > 0 ? { technicalsPerTeam: c.technicals } : {},   // 63.1o
    c.technicals > 0 && c.technicalArmament !== 'hmg' ? { technicalArmaments: c.technicalArmament === 'mix' ? (['hmg', 'rocket', 'missile'] as const) : [c.technicalArmament] } : {},   // 69.21
    c.mountStore !== 'default' ? { mountStore: Number(c.mountStore) } : {},   // 69.21 (240)
    c.hmgs > 0 ? { hmgsPerTeam: c.hmgs } : {},   // 65.22
    c.drones.squad > 0 ? { squadSize: 3, dronesByTeam: [c.drones.squad * Math.ceil(c.units / 3), c.drones.squad * Math.ceil(c.units / 3)] } : {},
    // the team picture: the bots share what they see (the map's markers) and act on it — every teammate's sightings and
    // the team's drones and towers (the Lab set only the sharing, so a drone's sightings never reached a bot; the owner, Oct 11)
    c.picture === true ? { sharedSight: true, knowledgeByTeam: [{ teamPicture: true }, { teamPicture: true }] } : Array.isArray(c.picture) ? { knowledgeByTeam: [c.picture[0] ? { teamPicture: true } : {}, c.picture[1] ? { teamPicture: true } : {}] } : {},   // 67.24: per side (the bots read each team's own)
    c.formation !== 'none' ? { formation: { shape: c.formation } } : {},
    c.commanders !== 'none' ? { commanders: [c.commanders, c.commanders] } : {},
    c.fluidSquads ? { fluidSquads: true } : {},
    c.coverAcross ? {} : { coverAcross: false },   // 70.3: on by default in the setup, only off is passed
    c.coverMoves ? {} : { coverMoves: false },   // 66.7; 66.19: on by default in the setup, only off is passed
    c.squadLead ? {} : { squadLead: false },   // 68.9: on by default in the setup (decision 148); only off is passed (skirmishes have squads)
    c.localSteer ? {} : { localSteer: false },   // 62.43;/ 300: on by default in the setup, only off is passed
    c.planetRoutes ? { planetRoutes: true } : {},   // §0.11 step C
    c.roads ? { roads: true } : {},   // 216 (4)
    c.realTiming ? {} : { realTiming: false },   // 216;/ 300: on by default in the setup, only off is passed
    c.jumpFeel ? { ...(c.jumpApex !== 'default' ? { jumpApex: Number(c.jumpApex) } : {}), ...(c.jumpGravity !== 'default' ? { jumpGravity: Number(c.jumpGravity) } : {}) } : { jumpFeel: false },   // 63.15;/ 300: on by default in the setup, only off is passed
    c.tactics ? { tactics: true } : {},
    c.callReserves && c.reserves > 0 ? { callReserves: true } : {},   // 68.4: only with reserves (so the default launch is the menu's)
    c.commanderPlays === true ? {} : c.commanderPlays === false ? { commanderPlays: false } : { commanderPlays: [...c.commanderPlays] as boolean[] },   // (per side: only what differs from on)
    c.technicalRole ? {} : { technicalRole: false },   // 63.1r: on by default in the setup; only off is passed
    c.smallCraters ? { smallCraters: true } : {},   // 63.11: off by default (the owner, Oct 13); only on is passed
    c.burstPush ? { burstPush: true } : {},   // §5.21 step 6
    c.blastModel ? {} : { blastModel: false },   // 69.65: on by default in the setup, only off is passed
    c.waveShips ? { waveShips: true } : {},   // DECISIONS 200 step 4
    c.waveShips && c.waveWalk !== '60' ? { waveWalkPeople: Number(c.waveWalk) } : {},   // 207's follow-up
    c.frontPull ? {} : { frontPull: false },   // 69.5f: on by default in the setup; only off is passed
    c.attention ? {} : { attention: false },   // 181: on by default in the setup; only off is passed
    c.commanderPicture ? {} : { commanderPicture: false },
    c.commanderSquads ? {} : { commanderSquads: false },   // 69.87: on by default in the setup, only off is passed
    c.efforts ? { efforts: true } : {},   // §0.11 step D   // 182 (3): on by default in the setup; only off is passed
    c.contactBelief ? { contactBelief: true } : {},   // 182 (2): off by default
    c.quietTier ? { quietTier: true } : {},   // 62.60 lever 2: off by default
    c.marchTier ? { marchTier: true } : {},   // 62.60 lever 2b
    c.steadyTier ? { steadyTier: true } : {},   // 62.60 lever 2c
    c.brainRate ? {} : { brainRate: false },   // 62.68: on by default in the setup, only off is passed
    c.lookPeriod !== '200' ? { lookPeriod: LOOK_PERIOD.ticks(Number(c.lookPeriod)) } : {},   // 62.60, the look period in contact: only a longer one is passed
    c.pictureDecay ? { pictureDecay: true } : {},   // 182 (1): off by default
    c.flankTakesPoint === true ? { flankTakesPoint: true } : Array.isArray(c.flankTakesPoint) && c.flankTakesPoint.some(Boolean) ? { flankTakesPoint: [...c.flankTakesPoint] as boolean[] } : {},   // 66.16: off by default; only on is passed (per side)
    c.standoffManoeuvre === true ? { standoffManoeuvre: true } : Array.isArray(c.standoffManoeuvre) && c.standoffManoeuvre.some(Boolean) ? { standoffManoeuvre: [...c.standoffManoeuvre] as boolean[] } : {},   // 66.15: off by default; only on is passed (per side)
    c.holdAndPush === false ? { holdAndPush: false } : Array.isArray(c.holdAndPush) && !c.holdAndPush.every(Boolean) ? { holdAndPush: [...c.holdAndPush] as boolean[] } : {},   // 66.13: on by default; only off is passed (per side)
    c.scoutRide ? {} : { scoutRide: false },   // 63.1t: on by default in the setup; only off is passed   // 68.22 (decision 159): on by default in the setup; only off is passed
    c.holding ? { holding: true } : {},
    c.gunRun ? { seatsUnderThreat: true } : {},
    c.emplacements > 0 && c.gunsAt === 'hand' ? { gunsAt: 'hand' as const } : {},
    c.emplacements > 0 && !c.gunStock ? { gunStock: false } : {},
    c.emplacements > 0 && !c.gunSpotting ? { gunSpotting: false } : {},
    c.botKit === false ? { botKit: false } : Array.isArray(c.botKit) ? { botKit: [...c.botKit] as boolean[] } : {},   // 67.24: per side
    guns(sideOf(c.guns, 0)) || guns(sideOf(c.guns, 1)) ? { gunsByTeam: [parseGunShares(sideOf(c.guns, 0)) ?? undefined, parseGunShares(sideOf(c.guns, 1)) ?? undefined] } : {},   // 65.13 / 65.14: each side's long guns
    (t => (parseLayout(sideOf(c.layout, 0), t) || parseLayout(sideOf(c.layout, 1), t) ? { layoutByTeam: [parseLayout(sideOf(c.layout, 0), t) ?? undefined, parseLayout(sideOf(c.layout, 1), t) ?? undefined], ...(c.squadTypes ? { squadTypes: t } : {}) } : {}))(mergedSquadTypes(c.squadTypes)),   // (65.34: over the custom types too)   // 65.32: each side's squad layout
    parsePlayerLoadout(c.loadout) || c.yourArmour !== 'none' ? { playerLoadout: { team: 0, weapons: parsePlayerLoadout(c.loadout) ?? [], ...(c.yourArmour !== 'none' ? { armour: armourIssueOf(c.yourArmour) } : {}) } } : {},   // 65.32: the player's own (the human is team 0's first unit)
    c.gl40Rounds !== 6 ? { gl40Rounds: c.gl40Rounds } : {},   // 65.31: the 40 mm's rounds a life
    c.gl40Speed !== 'default' ? { gl40Speed: Number(c.gl40Speed) } : {},   // 65.31: the lob's dials
    c.gl40Gravity !== 'default' ? { gl40Gravity: Number(c.gl40Gravity) } : {},
    c.gl40Wind !== 'default' ? { gl40Wind: Number(c.gl40Wind) } : {},
    c.playerFireRate !== '1' ? { playerFireRate: Number(c.playerFireRate) } : {},   // 65.37
    c.dropshipExit === 'loop' ? { dropshipExit: 'loop' as const } : {},   // 69.3b's follow-up
    c.skyRule !== 'route' ? { skyRule: c.skyRule } : {},   // 69.52 step 1: route by default, only another rule passed
    c.mountedAssault ? { mountedAssault: true } : {},   // 69.37 step 1: off by default
    c.mountedAssault && c.convoyAssembly ? { convoyAssembly: true } : {},   // DECISIONS 290 (1): off by default, with the mounted assault only
    c.shipPickup ? {} : { shipPickup: false },   // 69.18;/ 300: on by default in the setup, only off is passed
    c.hotRescue ? { hotRescue: true } : {},   // 69.62: off by default
    c.pilotRisk !== 'cautious' ? { pilotRisk: [SKY_RISK[c.pilotRisk], SKY_RISK[c.pilotRisk]] } : {},   // 69.52 step 2: cautious by default
    c.shipWeave !== 'default' ? { shipWeave: WEAVE_STEPS[c.shipWeave] } : {},   // 69.52 step 2
    c.gunships !== '1' ? { gunships: Number(c.gunships) } : {},   // 63.13; 300: one a side by default in the setup, so any other count is passed
    c.helicopters !== '1' ? { helicopters: Number(c.helicopters) } : {},   // 69.59 step 1; 300: one a side by default in the setup
    c.helicopters !== '0' && c.heliSuppressor ? { heliSuppressor: true } : {},   // 69.59 step 3 (286 (4)): off by default, with helicopters only
    c.helicopters !== '0' && c.helipad ? { helipad: true } : {},   // 69.74 step 2: off by default, with helicopters only
    c.gunshipGun !== '40' ? { gunshipGun: Number(c.gunshipGun) as GunshipGun } : {},   // 63.13: the 40 mm by default
    c.gunshipChin !== '30' ? { gunshipChin: Number(c.gunshipChin) as AutocannonCalibre } : {},   // 277 (2): the 30 mm by default
    sideOf(c.airstrikes, 0) !== '3' || sideOf(c.airstrikes, 1) !== '3' ? { airstrikesByTeam: [Number(sideOf(c.airstrikes, 0)), Number(sideOf(c.airstrikes, 1))] } : {},   // 68.39
    sideOf(c.dropships, 0) !== '0' || sideOf(c.dropships, 1) !== '0' ? { dropshipsByTeam: [Number(sideOf(c.dropships, 0)), Number(sideOf(c.dropships, 1))] } : {},   // 69.3b
    sideOf(c.pods, 0) !== '0' || sideOf(c.pods, 1) !== '0' ? { podsByTeam: [Number(sideOf(c.pods, 0)), Number(sideOf(c.pods, 1))] } : {},   // DECISIONS 200 step 5
    sideOf(c.airstrikeSpend, 0) !== 'even' || sideOf(c.airstrikeSpend, 1) !== 'even' ? { airstrikeSpend: [sideOf(c.airstrikeSpend, 0), sideOf(c.airstrikeSpend, 1)] } : {},
    labForceCompose(c),   // 69.49: off unless a side has a doctrine
    sideOf(c.armour, 0) !== 'none' || sideOf(c.armour, 1) !== 'none' ? { armourByTeam: [sideOf(c.armour, 0), sideOf(c.armour, 1)] } : {},   // 185
    c.hitLocations ? { ...(c.hitHead !== '2.5' ? { hitHead: Number(c.hitHead) } : {}), ...(c.hitLimbs !== '0.6' ? { hitLimbs: Number(c.hitLimbs) } : {}) } : { hitLocations: false },   // 65.27;/ 300: on by default in the setup, only off is passed
    c.extraction ? { extraction: true } : {},
    c.reserves > 0 ? { reserves: [c.reserves, c.reserves] } : {},
    c.front !== 'auto' ? { frontHeights: Number(c.front) } : {},
    c.relays || c.fobs || c.convoys || c.buildCosts ? { relays: true, drops: true } : {},   // 69.2d (69.4: FOBs need the web); 69.3a: a relay carries its drops (69.5c: the Lab never set them)
    c.pointDefence ? {} : { pointDefence: false },   // 69.9;/ 300: on by default in the setup, only off is passed
    c.airDefenceSites ? { airDefenceSites: true } : {},   // 69.50 step 2a
    c.siteRounds && c.airDefenceSites ? { siteRounds: true } : {},   // 69.50 step 2b: with the sites only
    c.countermeasures ? {} : { countermeasures: false },   // 69.58;/ 300: on by default in the setup, only off is passed
    c.shipTurrets ? {} : { shipTurrets: false },   // 69.58 (271 (7));/ 300: on by default in the setup, only off is passed
    c.pointDefence && !c.towedGuns ? { towedGuns: false } : {},   // 69.13 (a); DECISIONS 233: with Point defence only (the two together; core/defaultOn.ts towedGunsOn); 300: on by default in the setup, only off is passed
    c.lighters ? { lighters: true } : {},   // 69.10
    { startUnwrapped: [unwrappedShare(sideOf(c.unwrapped, 0)), unwrappedShare(sideOf(c.unwrapped, 1))] },   // 69.19: always, the row's own (the setup reads it only where a pool stands)
    c.fobs || c.convoys || c.buildCosts ? { fobs: true } : {},   // 69.4
    c.convoys || c.buildCosts ? { convoys: true } : {},   // 69.4 step 5
    c.droneDrops ? { droneDrops: true, supplyDronesByTeam: [Number(c.supplyDrones), Number(c.supplyDrones)] } : {},   // 69.32: off by default
    c.buildCosts ? { buildCosts: true } : {},   // 69.4 step 6b
    c.munitionsSupply ? { munitionsSupply: true } : {},   // 69.30 step 1
    c.roughStart ? { roughStart: true } : {},   // 69.2a
    c.storyObjectives ? { storyObjectives: true } : {},   // 69.50 step 1
    c.storyObjectives && c.storyCarry ? { storyCarry: true } : {},   // 69.61 step 4: with the story only
    c.storyObjectives && c.storyEscort ? { storyEscort: true } : {},   // 69.61 step 5: with the story only
    c.intelRecord ? { intelRecord: true } : {},   // 69.73 step 2a
    c.demoCharges ? { charges: true } : {},   // 69.47 step 1
    c.intelRecord && c.baseIntel !== 'none' ? { baseIntel: c.baseIntel } : {},   // 69.73 step 3
    c.hqHealth !== STRUCTURE_HEALTH ? { hqHealth: c.hqHealth } : {},   // 69.61 step 6 (DECISIONS 284 (a))
  ];
  // (classic is the setup's default: left out, as the page's URL path always left it — a match at either reads the same)
  return Object.assign(c.scale === 'classic' ? {} : { scale: c.scale }, ...parts);
}

/**
 * 69.6b / 69.6d: the Foothold rows as the skirmish's mode overrides (the page's URL reads the same, autoplay.ts) — the sectors,
 * and an Operations push over them (which turns the sectors on, at their default count, when the Sectors row is 0).
 */
export function footholdModes(sectors: number, operations: 'off' | 'push' | 'tug' | 'open', layout: string = 'even', wins: Partial<Record<WinConditionKind, boolean>> = {}, bleed = false, territory = false, nodes: { count?: number; share?: number } = {}): { modeOverrides?: Partial<ModeConfig> } {
  // §0.11 step A: the open layout (no sectors), won by any of the ways on (all by default)
  // (216: tickets the cap, spent on arrivals — the points' bleed off unless asked for)
  // (216 (4): territory, its field shown and — when asked — a way to win: off unless named)
  // (Oct 20, the owner's stand-in: nodes over the whole planet, and a share of them held as a way to win: off unless set)
  const use: WinConditionKind[] = [...WIN_CONDITIONS.use.filter(k => wins[k] !== false), ...(territory && wins.territory ? ['territory' as const] : []), ...(nodes.share !== undefined ? ['nodes' as const] : []), ...(wins.objectives ? ['objectives' as const] : [])];   // (69.50: the story's, off unless named)
  if (operations === 'open') return { modeOverrides: { pointLayout: 'open', ...(nodes.count !== undefined ? { pointSpread: 'planet' as const, pointCount: nodes.count } : {}), winConditions: { use, needed: WIN_CONDITIONS.needed, ...(nodes.share !== undefined ? { nodesShare: nodes.share } : {}) }, ...(bleed ? {} : { pointBleed: false }), ...(territory ? { territory: true } : {}) } };
  // 183 (4): a layout's pattern (its own sector count; it turns the sectors on as operations does)
  const pattern = sectorPatternOf(layout);
  if (sectors <= 0 && operations === 'off' && !pattern) return {};
  return { modeOverrides: { pointLayout: 'sectors', ...(sectors > 0 && !pattern ? { sectorCount: sectors } : {}), ...(pattern ? { sectorPattern: pattern } : {}), ...(operations === 'push' ? { operations: {} } : operations === 'tug' ? { operations: { shape: 'tug' } } : {}) } };
}

/** Oct 20: the Nodes rows' values as footholdModes takes them ('off', absent, or anything unreadable: none). */
export function nodesOf(count: string | null | undefined, share: string | null | undefined): { count?: number; share?: number } {
  const n = Number(count), f = Number(share);
  return { ...(count && count !== 'off' && Number.isInteger(n) && n >= 1 ? { count: n } : {}), ...(share && share !== 'off' && f > 0 && f <= 1 ? { share: f } : {}) };
}

/** 183 (4): a sector layout's objectives per sector ('2:3:2' → [2, 3, 2]); null for 'even' or anything unreadable. */
export function sectorPatternOf(layout: string | null | undefined): number[] | null {
  if (!layout || layout === 'even') return null;
  const xs = layout.split(':').map(Number);
  return xs.length >= 1 && xs.every(x => Number.isInteger(x) && x >= 1 && x <= 4) ? xs : null;
}

/** §0.11 step A (the owner, Oct 19: "Length configurable but sure start 30"): open Operations run OPEN_LAYOUT.matchSeconds unless the time row is set. */
export function openTime(c: Pick<LabConfig, 'operations' | 'time'>): number {
  return c.operations === 'open' && c.time === LAB_DEFAULTS.time ? OPEN_LAYOUT.matchSeconds : c.time;
}

/** Build the launch from a config, through the same setup builders the menu and the URL use. */
/** 69.19: a side's Unwrapped at start step as GameConfig.startUnwrapped's value (a share, or auto by the roles). */
/** 69.52 step 2, 69.66: the Lab's bend steps (the swing, people; `default` SKY_BENDS's). */
const WEAVE_STEPS = { off: 0, low: SKY_BENDS.swingPeople / 2, default: SKY_BENDS.swingPeople, high: SKY_BENDS.swingPeople * 1.6 } as const;
function unwrappedShare(v: 'auto' | 'all' | 'some' | 'none'): number | 'auto' {
  return v === 'auto' ? 'auto' : START_UNWRAPPED[v];
}

/**
 * The match the Lab's Play starts for `c`. `extra`: setup fields beside the rows (the page's URL path: a smoke case's
 * tickets, its scripted withdrawal and the like), over what the rows give; `time`: the time limit as given (the page's URL
 * takes it literally — the smoke's 20 s prefix is under the row's 30 s floor).
 */
export function labLaunch(c: LabConfig, extra: Partial<SkirmishSetup> = {}, time?: number): LabLaunch {
  const difficulty: string[] = Array.isArray(c.difficulty) ? [...c.difficulty] : [c.difficulty as string];   // 67.24: the enemy its own
  if (c.mode === 'skirmish') {
    const t = time ?? openTime(c);
    const setup = skirmishMatchSetup({ seed: c.seed, teams: 2, unitsPerTeam: c.units, difficulty, timeLimit: t, planetSize: c.planetSize, ...labExtras(c), ...footholdModes(c.sectors, c.operations, c.sectorLayout, { hq: c.winHq, majority: c.winMajority, tickets: c.winTickets, time: c.winTime, territory: c.winTerritory, objectives: c.winObjectives }, c.ticketBleed, c.territory, nodesOf(c.openNodes, c.winNodes)), ...extra });
    return { setup, humanTeam: 0, tickLimit: Math.round((t + 5) / PHYS_DT), range: false };
  }
  const range = c.mode === 'range', t = time ?? c.time;
  const setup: DuelSetup = { ...actionDuelSetup({ seed: c.seed, units: range ? 1 : c.units, ...(range ? { unitsByTeam: [1, RANGE_LAYOUT.length] } : {}), difficulty, timeLimit: t }), ...labExtras(c), ...extra };
  // The range's targets stand at 6, 9 and 12 units: it always runs classic (67.1), and calm (decision 147 turned weather and wind on for matches)
  if (range) { delete setup.scale; setup.weather = false; setup.planetPick = 'stars'; }   // (classic: the setup's default)
  return { setup, humanTeam: 0, tickLimit: Math.round(t / PHYS_DT), range };
}

/** The `?action&…` query the page reads for this config (autoplay.ts `actionLaunch` + `layer0Extras`). Defaults are left out. */
/**
 * 69.13 (d): a config with the retired rows (the gun pit's) at their defaults — what the Lab loads, takes from a preset and
 * launches when the page has no `legacy=1`, so a stored config or preset that had gun pits does not bring them back
 * behind rows the player cannot see.
 */
export function dropLegacy(c: LabConfig): LabConfig {
  const out = { ...c } as Record<string, unknown>;
  for (const r of LAB_SWITCHES) if (r.legacy) out[r.key] = r.default;
  return out as unknown as LabConfig;
}

export function labUrlParams(c: LabConfig): URLSearchParams {
  const p = new URLSearchParams();
  p.set('action', '');
  if (c.mode === 'skirmish') p.set('skirmish', '');
  if (c.mode === 'range') p.set('range', '');
  p.set('seed', c.seed);
  if (c.mode !== 'range') p.set('units', String(c.units));
  p.set('difficulty', sideOf(c.difficulty, 0));
  if (Array.isArray(c.difficulty)) p.set('difficultyenemy', c.difficulty[1]);
  p.set('time', String(c.time));
  if (c.mode === 'skirmish' && c.planetSize !== 'megaplanet') p.set('size', c.planetSize);
  if (c.scale === 'human' && c.mode !== 'range') p.set('scale', 'human');
  // every other row: its parameter, written only when it differs from the default (labRows.ts)
  for (const r of LAB_SWITCHES) {
    if (!r.url) continue;
    if (r.legacy && c[r.key] !== r.default) p.set(LEGACY_PARAM, '1');   // (69.13 (d): a retired row set keeps its way back)
    if (r.perTeam) { writeSided(p, r.key, c); continue; }
    if (r.kind === 'counts') {   // (62.58 step 3: each part its own parameter, written when it differs from its default)
      const v = c[r.key] as unknown as Record<string, number>, d = r.default as Record<string, number>;
      for (const { part, param } of r.parts) if (v[part] !== d[part]) p.set(param, String(v[part]));
      continue;
    }
    const v = c[r.key];
    if (v === r.default) continue;
    if (r.key === 'weatherCells') { p.set('weatherCells', v === 'cells' ? '1' : String(v)); continue; }   // (62.58 step 4: cells as the toggle wrote it, the page's reader)
    p.set(r.url.param, r.kind === 'toggle' ? (v ? '1' : '0') : r.url.pair ? `${v},${v}` : String(v));
  }
  return p;
}

/** 62.58 step 2: a per-team dial in the parameters 67.24's rows wrote (the page reads them: autoplay.ts, unchanged). */
function writeSided(p: URLSearchParams, key: keyof LabConfig, c: LabConfig): void {
  if (key === 'picture') {
    const [a, b] = [sideOf(c.picture, 0), sideOf(c.picture, 1)];
    if (!a && !b) p.set('picture', '0');
    else if (a !== b) p.set('picturesides', a ? 'mine' : 'enemy');
  } else if (key === 'botKit') {
    const [a, b] = [sideOf(c.botKit, 0), sideOf(c.botKit, 1)];
    if (!a || !b) p.set('kit', '0');
    if (a !== b) p.set('kitsides', a ? 'enemy' : 'mine');
  } else if (key === 'brain') {
    if (sideOf(c.brain, 0) !== 'default') p.set('brain', sideOf(c.brain, 0));
    if (Array.isArray(c.brain)) p.set('brainenemy', c.brain[1]);
  } else if (key === 'armour') {
    if (sideOf(c.armour, 0) !== 'none') p.set('armour', sideOf(c.armour, 0));
    if (Array.isArray(c.armour)) p.set('armourenemy', c.armour[1]);
  } else if (key === 'unwrapped') {
    if (sideOf(c.unwrapped, 0) !== 'auto') p.set('unwrapped', sideOf(c.unwrapped, 0));
    if (Array.isArray(c.unwrapped)) p.set('unwrappedenemy', c.unwrapped[1]);
  } else if (key === 'forceDoctrine') {
    if (sideOf(c.forceDoctrine, 0) !== 'off') p.set('doctrine', sideOf(c.forceDoctrine, 0));
    if (Array.isArray(c.forceDoctrine)) p.set('doctrineenemy', c.forceDoctrine[1]);
  } else if (key === 'forceBudget') {
    if (sideOf(c.forceBudget, 0) !== '1') p.set('budget', sideOf(c.forceBudget, 0));
    if (Array.isArray(c.forceBudget)) p.set('budgetenemy', c.forceBudget[1]);
  } else if (key === 'airstrikeSpend') {
    if (sideOf(c.airstrikeSpend, 0) !== 'even') p.set('airstrikespend', sideOf(c.airstrikeSpend, 0));
    if (Array.isArray(c.airstrikeSpend)) p.set('airstrikespendenemy', c.airstrikeSpend[1]);
  } else if (key === 'pods') {
    if (sideOf(c.pods, 0) !== '0') p.set('pods', sideOf(c.pods, 0));
    if (Array.isArray(c.pods)) p.set('podsenemy', c.pods[1]);
  } else if (key === 'dropships') {
    if (sideOf(c.dropships, 0) !== '0') p.set('dropships', sideOf(c.dropships, 0));
    if (Array.isArray(c.dropships)) p.set('dropshipsenemy', c.dropships[1]);
  } else if (key === 'airstrikes') {
    if (sideOf(c.airstrikes, 0) !== '3') p.set('airstrikes', sideOf(c.airstrikes, 0));   // (300: three by default)
    if (Array.isArray(c.airstrikes)) p.set('airstrikesenemy', c.airstrikes[1]);
  } else if (key === 'holdAndPush') {
    const [a, b] = [sideOf(c.holdAndPush, 0), sideOf(c.holdAndPush, 1)];
    if (!a && !b) p.set('holdpush', '0');
    else if (a !== b) p.set('holdpushsides', a ? 'mine' : 'enemy');
  } else if (key === 'flankTakesPoint') {
    const [a, b] = [sideOf(c.flankTakesPoint, 0), sideOf(c.flankTakesPoint, 1)];
    if (a || b) p.set('flanktake', '1');
    if (a !== b) p.set('flanktakesides', a ? 'mine' : 'enemy');
  } else if (key === 'standoffManoeuvre') {
    const [a, b] = [sideOf(c.standoffManoeuvre, 0), sideOf(c.standoffManoeuvre, 1)];
    if (a || b) p.set('standoff', '1');
    if (a !== b) p.set('standoffsides', a ? 'mine' : 'enemy');
  } else if (key === 'commanderPlays') {
    const [a, b] = [sideOf(c.commanderPlays, 0), sideOf(c.commanderPlays, 1)];
    if (!a && !b) p.set('plays', '0');
    else if (a !== b) p.set('playssides', a ? 'mine' : 'enemy');
  } else if (key === 'rockets') {
    const [a, b] = [sideOf(c.rockets, 0), sideOf(c.rockets, 1)];
    if (a || b) p.set('rockets', '1');
    if (a !== b) p.set('rocketsides', a ? 'mine' : 'enemy');
  } else if (key === 'guns') {
    if (sideOf(c.guns, 0) !== 'rifle') p.set('gunsmine', sideOf(c.guns, 0));
    if (sideOf(c.guns, 1) !== 'rifle') p.set('gunsenemy', sideOf(c.guns, 1));
  } else if (key === 'layout') {
    if (sideOf(c.layout, 0) !== 'none') p.set('layoutmine', sideOf(c.layout, 0));
    if (sideOf(c.layout, 1) !== 'none') p.set('layoutenemy', sideOf(c.layout, 1));
  }
}

/** `?action&skirmish&seed=…` as text (the flags' empty values dropped). */
export function labUrl(c: LabConfig, base = ''): string {
  return base + '?' + labUrlParams(c).toString().replace(/=(?=&|$)/g, '');
}

/** The config a `?action&…` query describes (the inverse of labUrlParams, with defaults for what it leaves out). */
export function labFromParams(p: URLSearchParams): LabConfig {
  const mode: LabMode = p.has('skirmish') ? 'skirmish' : p.has('range') ? 'range' : 'duel';
  return normalizeLabConfig({
    mode,
    scale: p.get('scale') === 'human' ? 'human' : 'classic',   // the page's URL default is classic (the menu's default is the toggle's, 67.1)
    units: p.get('units') ?? (mode === 'skirmish' ? 6 : 1),
    planetSize: p.get('size') ?? 'megaplanet',
    seed: p.get('seed') ?? (mode === 'skirmish' ? 'skirmish-1' : mode === 'range' ? 'range-1' : 'duel-1'),
    difficulty: (p.get('difficulty') ?? 'medium').split(',')[0],
    // 62.58 step 2: the per-side parameters 67.24's rows wrote, folded into the per-team dials (normalizeLabConfig)
    ...Object.fromEntries([['difficultyEnemy', 'difficultyenemy'], ['brainEnemy', 'brainenemy'], ['airstrikesEnemy', 'airstrikesenemy'], ['dropshipsEnemy', 'dropshipsenemy'], ['podsEnemy', 'podsenemy'], ['airstrikeSpendEnemy', 'airstrikespendenemy'], ['forceDoctrineEnemy', 'doctrineenemy'], ['forceBudgetEnemy', 'budgetenemy'], ['armourEnemy', 'armourenemy'], ['unwrappedEnemy', 'unwrappedenemy'], ['gunsEnemy', 'gunsenemy'], ['layoutEnemy', 'layoutenemy'], ['pictureSides', 'picturesides'], ['botKitSides', 'kitsides'], ['rocketSides', 'rocketsides'], ['playsSides', 'playssides'], ['holdPushSides', 'holdpushsides'], ['standoffSides', 'standoffsides'], ['flankTakeSides', 'flanktakesides']].map(([k, q]) => [k, p.get(q) ?? undefined])),
    gunsMine: p.get('gunsmine') ?? undefined,
    layoutMine: p.get('layoutmine') ?? undefined,
    // 62.58 step 3: a counts row from its parts' parameters (the HE's `grenades`, the smoke's `smoke`, as before)
    ...Object.fromEntries(LAB_SWITCHES.filter(r => r.kind === 'counts').map(r => [r.key, Object.fromEntries((r as { parts: ReadonlyArray<{ part: string; param: string }> }).parts.map(x => [x.part, p.get(x.param) ?? undefined]))])),
    time: p.get('time') ?? 600,
    // every other row from its parameter (a pair's first half); what the URL leaves out normalises to the default
    ...Object.fromEntries(LAB_SWITCHES.filter(r => r.url && r.kind !== 'counts').map(r => { const t = r.legacy && p.get(LEGACY_PARAM) !== '1' ? null : p.get(r.url!.param); return [r.key, t === null ? undefined : r.url!.pair ? t.split(',')[0] : t]; })),   // (69.13 (d): a retired row's parameter only beside legacy=1)
  });
}
